import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Percent, Wallet, Gift } from 'lucide-react';
import { DriverDetails } from '@/types/driver.types';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/formatters';
import { useI18n } from '@/contexts/I18nContext';

interface DriverPaymentInfoProps {
  driver: DriverDetails;
}

export function DriverPaymentInfo({ driver }: DriverPaymentInfoProps) {
  const { t } = useI18n();
  const isSalary = driver.payment_type === 'salary';
  const rate = parseFloat(String(driver.commission_rate || 0));
  const commissionPercentage = rate > 1 ? rate.toFixed(0) : (rate * 100).toFixed(0);
  const perTripBonus = Number(driver.per_trip_bonus ?? 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <CreditCard className="h-4 w-4" />
          معلومات الدفع
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Payment Type */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
              <Wallet className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t.financials.payment_type}</p>
              <p className="text-sm font-medium">{isSalary ? t.financials.salary_type : t.financials.commission_type}</p>
            </div>
          </div>
          <Badge
            variant="outline"
            className={cn(
              'text-xs',
              isSalary
                ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800'
                : 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800'
            )}
          >
            {isSalary ? t.financials.salary_type : t.financials.commission_type}
          </Badge>
        </div>

        {/* Commission Rate */}
        {!isSalary && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                <Percent className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t.financials.commission_rate}</p>
                <p className="text-sm font-medium">{commissionPercentage}%</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-100 dark:bg-purple-950">
              <span className="text-lg font-bold text-purple-700 dark:text-purple-300">
                {commissionPercentage}%
              </span>
            </div>
          </div>
        )}

        {/* Per-trip bonus */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
              <Gift className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t.financials.per_trip_bonus}</p>
              <p className="text-sm font-medium">{formatCurrency(perTripBonus)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-950">
            <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
              {formatCurrency(perTripBonus)}
            </span>
          </div>
        </div>

        {/* Salary indicator */}
        {isSalary && (
          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              هذا السائق يتقاضى راتباً ثابتاً بدون عمولة على الشحنات
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
